import "./index.scss";
import React, { FunctionComponent, useState, ChangeEvent, useEffect } from "react";
import WizardContainer, { Invocations, SelectedWidget } from "../../containers/Wizard";
import { DURATION_SELECTION_PATH, PAYMENT_INFORMATION_PATH } from "../../routing/paths";
import ButtonOptions from "../ButtonOptions";
import OrigamiPreview from "../OrigamiPreview";
import { noNormalizer } from "../../shared/normalizers";
import { tomorrowDate } from "../../shared/dates";
import { Tab } from "@own-local/ad-unit/lib/AdUnit";
import { isUrlValid } from "../../shared/validations"

export const ORIGAMI_DATA_SPELLS = [
  "business",
  "business_name",
  "caption",
  "background",
  "logo",
  "layout",
  "address",
  "city",
  "zip",
  "business_phone",
  "website",
  "facebook",
  "twitter",
  "instagram",
  "additional_text",
  "email",
  "state",
];

const ORIGAMI_DATA_PREPENDS_MAP: OrigamiDataType = {
  facebook: "https://facebook.com/",
  twitter: "https://twitter.com/",
  instagram: "https://instagram.com/",
};

export type BusinessDataType = {
  uuid: string;
  name: string;
};

export type OrigamiDataType = {
  [key: string]: string | object | undefined;
};

type StartDateStepProps = {
  invocate: Function;
  invocations: Invocations;
  changePath: Function;
};

const defineOrigamiData = (invocations: Invocations): OrigamiDataType => {
  return ORIGAMI_DATA_SPELLS.reduce<OrigamiDataType>(
    (origamiData, spell): OrigamiDataType => {
      const prependValue = ORIGAMI_DATA_PREPENDS_MAP[spell] || "";
      if (invocations[spell]) origamiData[spell] = `${prependValue}${invocations[spell]}`;

      if (spell === "logo") {
        origamiData["logo"] = isUrlValid(invocations["logo"]?.toString()) ? invocations["logo"] : ""
      }

      return origamiData;
    },
    {},
  );
};

export const StartDateStep: FunctionComponent<StartDateStepProps> = ({
  invocate,
  invocations,
  changePath,
}: StartDateStepProps) => {
  const [origamiData, setOrigamiData] = useState<OrigamiDataType>(
    defineOrigamiData(invocations),
  );
  const selectedWidget = invocations["widget"] as SelectedWidget;
  const selectedDuration = String(invocations["duration"]);
  const startDate = tomorrowDate.toLocaleDateString("en-CA");
  const [input, setInput] = useState<HTMLInputElement | HTMLTextAreaElement | null>();
  const initialValue = noNormalizer(String(invocations["start_date"] || ""));
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, setValue]);

  useEffect(() => {
    setOrigamiData(defineOrigamiData(invocations));
  }, [setOrigamiData, invocations]);

  const onChange = (val: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = noNormalizer(val.target.value);
    setValue(value);
  };

  const handleSubmit = () => {
    if (!input?.reportValidity()) return;
    if (initialValue === value) changePath(PAYMENT_INFORMATION_PATH);
    else invocate("start_date", value, PAYMENT_INFORMATION_PATH);
  };

  const inputProps = {
    ref: (el: HTMLInputElement | HTMLTextAreaElement | null) => setInput(el),
    type: "date",
    placeholder: startDate,
    min: startDate,
    value: value,
    onChange: onChange,
    required: true,
    autoFocus: true,
  };

  return (
    <div>
      <OrigamiPreview
        spell="start_date"
        origamiData={origamiData}
        selectedTab={Tab.Info}
        showExpanded={false}
      />
      <div className="question">
        <h3>
          When do you want your ad to <strong>start</strong> appearing on our website?
        </h3>
        <h4>
          Your ad will run for <strong>{selectedDuration}</strong> week(s) at $
          <strong>{selectedWidget?.price}</strong>/week.
        </h4>

        <input {...inputProps} />

        <ButtonOptions
          changePath={changePath}
          previous={DURATION_SELECTION_PATH}
          handleSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default WizardContainer(StartDateStep);
